/**
 * Analytics wrapper around PostHog.
 * All app code goes through these helpers, never posthog directly.
 * Silent no-op unless REACT_APP_POSTHOG_KEY is set (and Do Not Track is off).
 *
 * Session recording: ENABLED (decision 2026-07-07) — free-tier quota, and
 * watching real sessions is the fastest way to understand grow-first users.
 * maskAllInputs keeps every typed value (compose-box drafts especially —
 * writers' unpublished work is sensitive) out of recordings; the DNT guard
 * above applies to recordings too since init never runs under DNT.
 * Recording also requires the project-level toggle in PostHog
 * (Settings -> Session replay -> Record user sessions).
 */

import posthog from 'posthog-js';

// True only after a successful init; every helper checks this first.
let enabled = false;

// Respect the browser's Do Not Track signal (string '1' or boolean true).
const dntEnabled = () =>
  typeof navigator !== 'undefined' &&
  (navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes' || window.doNotTrack === '1');

/** Initialise PostHog once at app startup. No-op without a key or when DNT is on. */
export function initAnalytics() {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key || dntEnabled()) return;
  posthog.init(key, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
    defaults: '2025-05-24', // PostHog defaults incl. automatic SPA pageviews
    session_recording: { maskAllInputs: true } // never record typed text (drafts!)
  });
  enabled = true;
}

/** Capture a named event with optional properties. */
export function capture(event, props) {
  if (enabled) posthog.capture(event, props);
}

/** Tie subsequent events to the logged-in user (id + username only). */
export function identify(id, props) {
  if (enabled && id) posthog.identify(String(id), props);
}

/** Clear the identified user (call on logout). */
export function reset() {
  if (enabled) posthog.reset();
}
